import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { closeSnackbar, type SnackbarKey, SnackbarProvider } from 'notistack';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface NotificationsProviderProps {
  children: ReactNode;
}

/**
 * App-level provider for transient toast notifications.
 *
 * Thin wrapper around `notistack` so consumers only depend on the
 * `useNotify()` hook — the underlying library can be swapped without
 * touching feature code.
 */
export function NotificationsProvider({ children }: NotificationsProviderProps) {
  return (
    <SnackbarProvider
      maxSnack={4}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={4500}
      preventDuplicate
      action={(key) => <DismissAction snackbarKey={key} />}
    >
      {children}
    </SnackbarProvider>
  );
}

function DismissAction({ snackbarKey }: { snackbarKey: SnackbarKey }) {
  const { t } = useTranslation();
  return (
    <IconButton
      size="small"
      color="inherit"
      aria-label={t('common.dismissNotification')}
      onClick={() => {
        closeSnackbar(snackbarKey);
      }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );
}
